// frontend/src/services/enhancedReportsService.js
import api from './api';

export const enhancedReportsService = {
  // ============================================================================
  // SERVER-RENDERED PDF (TASK-038/039) - overtime and leave
  // ============================================================================

  /**
   * Fetch a report model (rendered by components/reports/ReportView.vue).
   * @param {'overtime'|'leave'|'attendance'|'late-arrivals'|'payroll'|'short-leave'} type
   * @param {'individual'|'general'} scope
   * @param {Object} params - employeeId (user id), startDate, endDate, lang, rateMonth
   */
  async getReportView(type, scope, params = {}) {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    const response = await api.get(`/admin/enhanced-reports/${scope}/${type}/view`, { params: clean, timeout: 60000 });
    return response.data.data;
  },

  /**
   * Download a server-rendered PDF report and save it in the browser.
   * @param {string} dataType - report type slug (see getReportView)
   * @param {'individual'|'general'} scope
   * @param {Object} params - employeeId (user id), startDate, endDate, lang, rateMonth
   */
  async downloadReportPdf(dataType, scope, params = {}) {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    let response;
    try {
      response = await api.get(`/admin/enhanced-reports/${scope}/${dataType}/pdf`, {
        params: clean,
        responseType: 'blob',
        timeout: 120000
      });
    } catch (error) {
      // Error bodies arrive as a Blob because of responseType; surface the JSON message
      const blob = error.response?.data;
      if (blob instanceof Blob) {
        try {
          const body = JSON.parse(await blob.text());
          throw new Error(body.message || body.error?.message || 'Failed to generate PDF');
        } catch (parseErr) {
          if (parseErr instanceof SyntaxError) throw new Error('Failed to generate PDF');
          throw parseErr;
        }
      }
      throw error;
    }

    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${dataType}_report.pdf`;

    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return filename;
  },

  // ============================================================================
  // INDIVIDUAL REPORTS - Detailed records for a single employee
  // ============================================================================

  /**
   * Get Individual Overtime Report
   * Shows detailed overtime records with clock-in/out times and reasons
   */
  async getIndividualOvertimeReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/individual/overtime', { params });
      return response.data;
    } catch (error) {
      console.error('Get individual overtime report error:', error);
      throw error;
    }
  },

  /**
   * Get Individual Attendance Report
   * Shows detailed attendance records with all clock-ins/outs and session details
   */
  async getIndividualAttendanceReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/individual/attendance', { params });
      return response.data;
    } catch (error) {
      console.error('Get individual attendance report error:', error);
      throw error;
    }
  },

  /**
   * Get Individual Leave Report
   * Shows detailed leave requests with reasons and approval history
   */
  async getIndividualLeaveReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/individual/leave', { params });
      return response.data;
    } catch (error) {
      console.error('Get individual leave report error:', error);
      throw error;
    }
  },

  // ============================================================================
  // GENERAL REPORTS - Summary for all employees
  // ============================================================================

  /**
   * Get General Overtime Report
   * Shows overtime hours summary for all employees
   */
  async getGeneralOvertimeReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/general/overtime', { params });
      return response.data;
    } catch (error) {
      console.error('Get general overtime report error:', error);
      throw error;
    }
  },

  /**
   * Get General Attendance Report
   * Shows work hours summary for all employees
   */
  async getGeneralAttendanceReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/general/attendance', { params });
      return response.data;
    } catch (error) {
      console.error('Get general attendance report error:', error);
      throw error;
    }
  },

  /**
   * Get General Leave Report
   * Shows leave days summary for all employees
   */
  async getGeneralLeaveReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/general/leave', { params });
      return response.data;
    } catch (error) {
      console.error('Get general leave report error:', error);
      throw error;
    }
  },

  async getIndividualLateArrivalReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/individual/late-arrivals', { params });
      return response.data;
    } catch (error) {
      console.error('Get individual late arrival report error:', error);
      throw error;
    }
  },

  async getGeneralLateArrivalReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/general/late-arrivals', { params });
      return response.data;
    } catch (error) {
      console.error('Get general late arrival report error:', error);
      throw error;
    }
  },

  async getIndividualPayrollReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/individual/payroll', { params });
      return response.data;
    } catch (error) {
      console.error('Get individual payroll report error:', error);
      throw error;
    }
  },

  async getGeneralPayrollReport(params = {}) {
    try {
      const response = await api.get('/admin/enhanced-reports/general/payroll', { params });
      return response.data;
    } catch (error) {
      console.error('Get general payroll report error:', error);
      throw error;
    }
  },

  // ============================================================================
  // UNIFIED REPORT GENERATOR
  // ============================================================================

  /**
   * Generate Report based on type and scope
   * @param {Object} config - Report configuration
   * @param {string} config.reportType - 'individual' or 'general'
   * @param {string} config.dataType - 'overtime', 'attendance', or 'leave'
   * @param {string} config.employeeId - Required for individual reports
   * @param {string} config.startDate - Start date (YYYY-MM-DD)
   * @param {string} config.endDate - End date (YYYY-MM-DD)
   * @param {string} config.department - Department filter (optional)
   * @param {string} config.status - Status filter (optional)
   */
  async generateReport(config = {}) {
    try {
      const {
        reportType = 'general', // 'individual' or 'general'
        dataType = 'attendance', // 'overtime', 'attendance', or 'leave'
        employeeId,
        startDate,
        endDate,
        department,
        status
      } = config;

      // Validate required parameters
      if (reportType === 'individual' && !employeeId) {
        throw new Error('Employee ID is required for individual reports');
      }

      const params = {
        employeeId,
        startDate,
        endDate,
        department,
        status
      };

      // Call the appropriate endpoint based on report type and data type
      let reportData;

      if (reportType === 'individual') {
        switch (dataType) {
          case 'overtime':
            reportData = await this.getIndividualOvertimeReport(params);
            break;
          case 'attendance':
            reportData = await this.getIndividualAttendanceReport(params);
            break;
          case 'leave':
            reportData = await this.getIndividualLeaveReport(params);
            break;
          case 'lateArrivals':
            reportData = await this.getIndividualLateArrivalReport(params);
            break;
          case 'payroll':
            reportData = await this.getIndividualPayrollReport(params);
            break;
          default:
            throw new Error(`Unsupported data type: ${dataType}`);
        }
      } else if (reportType === 'general') {
        switch (dataType) {
          case 'overtime':
            reportData = await this.getGeneralOvertimeReport(params);
            break;
          case 'attendance':
            reportData = await this.getGeneralAttendanceReport(params);
            break;
          case 'leave':
            reportData = await this.getGeneralLeaveReport(params);
            break;
          case 'lateArrivals':
            reportData = await this.getGeneralLateArrivalReport(params);
            break;
          case 'payroll':
            reportData = await this.getGeneralPayrollReport(params);
            break;
          default:
            throw new Error(`Unsupported data type: ${dataType}`);
        }
      } else {
        throw new Error(`Unsupported report type: ${reportType}`);
      }

      return {
        success: true,
        data: reportData.data,
        reportType: reportData.reportType,
        config: {
          reportType,
          dataType,
          ...params
        },
        generatedAt: new Date().toISOString()
      };

    } catch (error) {
      console.error('Generate report error:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT FUNCTIONS
  // ============================================================================

  /**
   * Export report to CSV
   */
  async exportReportToCSV(config = {}) {
    try {
      const report = await this.generateReport(config);
      const { reportType, dataType } = config;
      const data = report.data;

      let csv = '';
      let filename = '';

      // Generate CSV based on report type and data type
      if (reportType === 'individual') {
        switch (dataType) {
          case 'overtime':
            csv = this.generateOvertimeIndividualCSV(data);
            filename = `individual-overtime-${data.employee?.employeeCode}-${new Date().toISOString().split('T')[0]}.csv`;
            break;
          case 'attendance':
            csv = this.generateAttendanceIndividualCSV(data);
            filename = `individual-attendance-${data.employee?.employeeCode}-${new Date().toISOString().split('T')[0]}.csv`;
            break;
          case 'leave':
            csv = this.generateLeaveIndividualCSV(data);
            filename = `individual-leave-${data.employee?.employeeCode}-${new Date().toISOString().split('T')[0]}.csv`;
            break;
        }
      } else {
        switch (dataType) {
          case 'overtime':
            csv = this.generateOvertimeGeneralCSV(data);
            filename = `general-overtime-${new Date().toISOString().split('T')[0]}.csv`;
            break;
          case 'attendance':
            csv = this.generateAttendanceGeneralCSV(data);
            filename = `general-attendance-${new Date().toISOString().split('T')[0]}.csv`;
            break;
          case 'leave':
            csv = this.generateLeaveGeneralCSV(data);
            filename = `general-leave-${new Date().toISOString().split('T')[0]}.csv`;
            break;
        }
      }

      // Create and download CSV file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true, message: 'Report exported successfully' };

    } catch (error) {
      console.error('Export report to CSV error:', error);
      throw error;
    }
  },

  // CSV Generation Helpers
  generateOvertimeIndividualCSV(data) {
    let csv = 'Date,Start Time,End Time,Total Hours,Overtime Type,Reason,Status,Approver,Cost\n';
    data.records.forEach(record => {
      csv += `"${record.requestDate}","${record.startTime}","${record.endTime}","${record.totalHours}","${record.overtimeType}","${record.reason || ''}","${record.status}","${record.approverName || ''}","${record.actualCost || '0'}"\n`;
    });
    return csv;
  },

  generateAttendanceIndividualCSV(data) {
    let csv = 'Date,Clock In,Clock Out,Total Hours,Break Hours,Overtime Hours,Status,Location,Notes\n';
    data.dailySessions.forEach(session => {
      csv += `"${session.date}","${session.clockInTime || ''}","${session.clockOutTime || ''}","${session.totalHours}","${session.breakHours}","${session.overtimeHours}","${session.isLate ? 'Late' : 'On Time'}","${session.location?.address || 'Office'}","${session.notes || ''}"\n`;
    });
    return csv;
  },

  generateLeaveIndividualCSV(data) {
    let csv = 'Start Date,End Date,Days,Leave Type,Reason,Status,Approver,Is Emergency\n';
    data.records.forEach(record => {
      csv += `"${record.startDate}","${record.endDate}","${record.quantity}","${record.leaveType.name}","${record.reason || ''}","${record.status}","${record.approverName || ''}","${record.isEmergency ? 'Yes' : 'No'}"\n`;
    });
    return csv;
  },

  generateOvertimeGeneralCSV(data) {
    let csv = 'Employee Name,Employee Code,Department,Position,Total Overtime Hours,Total Cost,Before Hours,After Hours,Weekend Hours,Holiday Hours\n';
    data.employees.forEach(emp => {
      csv += `"${emp.employeeName}","${emp.employeeCode}","${emp.department}","${emp.position}","${emp.totalOvertimeHours}","${emp.totalCost}","${emp.breakdown.beforeHours}","${emp.breakdown.afterHours}","${emp.breakdown.weekendHours}","${emp.breakdown.holidayHours}"\n`;
    });
    return csv;
  },

  generateAttendanceGeneralCSV(data) {
    let csv = 'Employee Name,Employee Code,Department,Position,Days Present,Total Work Hours,Attendance Rate,Field Work Days,Overtime Days\n';
    data.employees.forEach(emp => {
      csv += `"${emp.employeeName}","${emp.employeeCode}","${emp.department}","${emp.position}","${emp.daysPresent}","${emp.totalWorkHours}","${emp.attendanceRate}%","${emp.fieldWorkDays}","${emp.overtimeDays}"\n`;
    });
    return csv;
  },

  generateLeaveGeneralCSV(data) {
    let csv = 'Employee Name,Employee Code,Department,Position,Total Leave Days,Pending Days,Emergency Leaves,Short Leaves\n';
    data.employees.forEach(emp => {
      csv += `"${emp.employeeName}","${emp.employeeCode}","${emp.department}","${emp.position}","${emp.totalLeaveDaysTaken}","${emp.pendingDays}","${emp.emergencyLeaves}","${emp.shortLeaves}"\n`;
    });
    return csv;
  }
};

export default enhancedReportsService;
